import { useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, UploadSimple, FileText, Download, Check, Warning, ListBullets } from "@phosphor-icons/react";
import { toast } from "sonner";

const SAMPLE_CSV = `name,email,company_name,phone,activate_maintenance,delivery_method,note
Jane Smith,jane@business.com,Acme Co.,+1 555 123 4567,true,setup_link,Existing 2024 maintenance customer
Robert Lee,rob@studio.io,Lee Studio,,false,setup_link,
Maria Chen,maria@gallery.com,Chen Gallery,5552223333,true,temp_password,Hosted on Cloudflare`;

const TRUTHY = new Set(["true", "yes", "1", "y", "t"]);

/** Parse a CSV string into {headers, rows}. Handles quoted cells with commas + newlines. */
function parseCSV(text) {
  const rows = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') { field += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { field += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ",") { cur.push(field); field = ""; }
      else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && next === "\n") i++;
        cur.push(field);
        rows.push(cur);
        cur = [];
        field = "";
      } else { field += ch; }
    }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  // Drop trailing empty rows
  while (rows.length > 0 && rows[rows.length - 1].every((c) => !c.trim())) rows.pop();
  if (rows.length === 0) return { headers: [], rows: [] };
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const dataRows = rows.slice(1).map((cells) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = (cells[idx] || "").trim();
    });
    return obj;
  });
  return { headers, rows: dataRows };
}

function rowToPayload(r) {
  return {
    name: r.name || "",
    email: r.email || "",
    company_name: r.company_name || r.company || "",
    phone: r.phone || "",
    delivery_method: (r.delivery_method || "setup_link").toLowerCase(),
    activate_maintenance: TRUTHY.has(String(r.activate_maintenance || "").toLowerCase().trim()),
    note: r.note || "",
  };
}

export function BulkImportClientsModal({ open, onClose, onCompleted }) {
  const [parsed, setParsed] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setParsed(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsedCsv = parseCSV(text);
      if (parsedCsv.rows.length === 0) {
        toast.error("CSV has no data rows");
        return;
      }
      if (!parsedCsv.headers.includes("name") || !parsedCsv.headers.includes("email")) {
        toast.error("CSV must include at least 'name' and 'email' columns");
        return;
      }
      setParsed({ ...parsedCsv, fileName: file.name });
      setResult(null);
    } catch (_) {
      toast.error("Could not read CSV");
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vision-grafix-clients-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const startImport = async () => {
    if (!parsed) return;
    const rows = parsed.rows.map(rowToPayload);
    setImporting(true);
    try {
      const { data } = await api.post("/admin/clients/bulk", { rows });
      setResult(data);
      const msg = `Imported ${data.created_count} · Skipped ${data.skipped_count} · Failed ${data.failed_count}`;
      if (data.created_count > 0) toast.success(msg);
      else toast.error(msg);
      if (data.created_count > 0) onCompleted?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto" data-testid="bulk-import-modal">
      <div className="border border-[#FF2D2D] bg-[#0a0a0a] w-full max-w-2xl p-6 my-8">
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Bulk import</div>
            <h3 className="font-display text-2xl mt-1">Import clients from CSV</h3>
            <div className="text-xs text-[#a3a3a3] mt-1">Onboard up to 500 customers at once. We'll email each one a portal access link.</div>
          </div>
          <button onClick={onClose} className="text-[#a3a3a3] hover:text-white" aria-label="Close"><X size={18} /></button>
        </div>

        {!result && (
          <>
            {/* Required columns */}
            <div className="border border-[#262626] bg-[#050505] p-4 mb-4">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] flex items-center gap-1.5">
                  <ListBullets size={11} /> Expected columns
                </div>
                <button
                  onClick={downloadSample}
                  data-testid="bulk-download-sample"
                  className="btn-outline px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-2"
                >
                  <Download size={12} /> Sample CSV
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                <div><span className="text-[#FF2D2D] font-bold">name</span> <span className="text-[#a3a3a3]">— required</span></div>
                <div><span className="text-[#FF2D2D] font-bold">email</span> <span className="text-[#a3a3a3]">— required, unique</span></div>
                <div><span className="text-white">company_name</span> <span className="text-[#a3a3a3]">— optional</span></div>
                <div><span className="text-white">phone</span> <span className="text-[#a3a3a3]">— optional</span></div>
                <div><span className="text-white">activate_maintenance</span> <span className="text-[#a3a3a3]">— true/false</span></div>
                <div><span className="text-white">delivery_method</span> <span className="text-[#a3a3a3]">— setup_link or temp_password</span></div>
                <div className="sm:col-span-2"><span className="text-white">note</span> <span className="text-[#a3a3a3]">— optional internal note</span></div>
              </div>
            </div>

            {/* File picker */}
            {!parsed ? (
              <label className="flex flex-col items-center justify-center gap-2 p-10 border-2 border-dashed border-[#262626] hover:border-[#FF2D2D] cursor-pointer text-[#a3a3a3] hover:text-white transition-colors">
                <UploadSimple size={28} />
                <span className="text-sm font-semibold">Click to choose a CSV file</span>
                <span className="text-xs">UTF-8 encoded · max 500 rows</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={onPickFile}
                  data-testid="bulk-file-input"
                  className="hidden"
                />
              </label>
            ) : (
              <div className="border border-[#FF2D2D] bg-[#FF2D2D]/5 p-4" data-testid="bulk-preview">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={18} className="text-[#FF2D2D] shrink-0" />
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{parsed.fileName}</div>
                      <div className="text-xs text-[#a3a3a3]">{parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"} · headers: {parsed.headers.join(", ")}</div>
                    </div>
                  </div>
                  <button
                    onClick={reset}
                    data-testid="bulk-reset"
                    className="text-xs text-[#a3a3a3] hover:text-[#FF2D2D] underline"
                  >
                    Pick a different file
                  </button>
                </div>
                {/* First-3 row preview */}
                <div className="bg-[#050505] border border-[#262626] overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#0f0f0f] text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Company</th>
                        <th className="px-3 py-2 text-left">Plan</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#262626]">
                      {parsed.rows.slice(0, 5).map((r, i) => {
                        const p = rowToPayload(r);
                        return (
                          <tr key={i}>
                            <td className="px-3 py-2 text-[#a3a3a3] tabular-nums">{i + 1}</td>
                            <td className="px-3 py-2">{p.name || <span className="text-[#FF2D2D]">missing</span>}</td>
                            <td className="px-3 py-2 text-[#a3a3a3] truncate max-w-[200px]">{p.email || <span className="text-[#FF2D2D]">missing</span>}</td>
                            <td className="px-3 py-2 text-[#a3a3a3]">{p.company_name || "—"}</td>
                            <td className="px-3 py-2">{p.activate_maintenance ? <span className="text-[#FF2D2D] font-bold">YES</span> : <span className="text-[#666]">no</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {parsed.rows.length > 5 && (
                  <div className="text-xs text-[#a3a3a3] mt-2">+ {parsed.rows.length - 5} more row{parsed.rows.length - 5 === 1 ? "" : "s"} not shown</div>
                )}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={startImport}
                disabled={!parsed || importing}
                data-testid="bulk-import-submit"
                className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50"
              >
                {importing ? "Importing…" : parsed ? `Import ${parsed.rows.length} client${parsed.rows.length === 1 ? "" : "s"} →` : "Choose a CSV first"}
              </button>
              <button onClick={onClose} className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider">Cancel</button>
            </div>
          </>
        )}

        {/* Result view */}
        {result && (
          <div data-testid="bulk-result">
            <div className="grid grid-cols-3 gap-px bg-[#262626] border border-[#262626] mb-4">
              <div className="bg-[#0a0a0a] p-4 text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">Created</div>
                <div className="font-display text-3xl mt-1 text-[#22c55e] tabular-nums">{result.created_count}</div>
              </div>
              <div className="bg-[#0a0a0a] p-4 text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">Skipped</div>
                <div className="font-display text-3xl mt-1 text-[#FF2D2D] tabular-nums">{result.skipped_count}</div>
              </div>
              <div className="bg-[#0a0a0a] p-4 text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">Failed</div>
                <div className="font-display text-3xl mt-1 text-[#a3a3a3] tabular-nums">{result.failed_count}</div>
              </div>
            </div>

            {result.created.length > 0 && (
              <div className="border border-[#22c55e]/30 bg-[#22c55e]/5 p-3 mb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#22c55e] mb-2 flex items-center gap-1.5">
                  <Check size={11} weight="bold" /> Created
                </div>
                <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
                  {result.created.map((r) => (
                    <div key={r.id} className="flex justify-between gap-3">
                      <span className="text-white truncate">{r.name}</span>
                      <span className="text-[#a3a3a3] truncate">{r.email}</span>
                      {r.has_maintenance_plan && <span className="text-[#FF2D2D] text-[10px] uppercase">Plan</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(result.skipped.length > 0 || result.failed.length > 0) && (
              <div className="border border-[#FF2D2D]/30 bg-[#FF2D2D]/5 p-3 mb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF2D2D] mb-2 flex items-center gap-1.5">
                  <Warning size={11} weight="bold" /> Issues
                </div>
                <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
                  {[...result.skipped, ...result.failed].map((r, i) => (
                    <div key={i} className="flex justify-between gap-3">
                      <span className="text-[#a3a3a3]">Row {r.row_index + 1}{r.email ? ` · ${r.email}` : ""}</span>
                      <span className="text-[#FF2D2D]">{r.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { reset(); }}
                data-testid="bulk-import-another"
                className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider"
              >
                Import another file
              </button>
              <button
                onClick={onClose}
                data-testid="bulk-import-done"
                className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
